import TopicFilterDropdown from '@src/components/common/TopicFilterDropdown';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import type { TopicMappingStatus } from './useTopicParagraphMapping';
import type { TopicFilterMode } from '@src/components/common/TopicFilterDropdown';

interface StoryTopicsFilterSlotProps {
  /** Transcript's Highlights filter owns Show Only, via the filters slot. */
  highlightsShowOnlyActive: boolean;
  topics: string[];
  selectedTopics: string[];
  setSelectedTopics: React.Dispatch<React.SetStateAction<string[]>>;
  mode: TopicFilterMode;
  onModeChange: (mode: TopicFilterMode) => void;
  matchCounts?: ReadonlyMap<string, number>;
  status: TopicMappingStatus;
  /** Prev/next over the matched paragraphs, fused to the trigger. */
  matchNav?: {
    current: number;
    total: number;
    onPrev: () => void;
    onNext: () => void;
  };
}

/**
 * The Story Topics half of Transcript's filters slot (PLAT-11182). A component
 * rather than inline JSX so it can carry an effect: entering Highlights' Show
 * Only clears the topic selection — its washes and chips — the mirror of the
 * highlight wash clearing when Story Topics takes the mode. The trigger is
 * inert for as long as the mode holds, so the selection cannot quietly return.
 */
export const StoryTopicsFilterSlot: React.FC<StoryTopicsFilterSlotProps> = ({
  highlightsShowOnlyActive,
  topics,
  selectedTopics,
  setSelectedTopics,
  mode,
  onModeChange,
  matchCounts,
  status,
  matchNav,
}) => {
  useEffect(() => {
    if (!highlightsShowOnlyActive) return;
    // Value-conditioned so the clear is a no-op re-run once empty (the flag
    // can't flip back while the trigger is inert).
    setSelectedTopics(prev => (prev.length > 0 ? [] : prev));
  }, [highlightsShowOnlyActive, setSelectedTopics]);

  if (status === 'loading') {
    return <TopicStatusLabel>Analyzing topics…</TopicStatusLabel>;
  }
  if (status === 'error') {
    return (
      <TopicStatusLabel title="Topic analysis unavailable for this story">
        Topic analysis unavailable
      </TopicStatusLabel>
    );
  }
  if (status !== 'ready') return null;
  return (
    <TopicFilterDropdown
      topics={topics}
      selectedTopics={selectedTopics}
      onApply={setSelectedTopics}
      mode={mode}
      onModeChange={onModeChange}
      matchCounts={matchCounts}
      matchNav={matchNav}
      disabledReason={
        highlightsShowOnlyActive
          ? 'Turn off "Show Only" in Highlights to filter by story topics'
          : null
      }
    />
  );
};

const TopicStatusLabel = styled.span`
  font-size: 12px;
  color: var(--text-muted);
  font-style: italic;
  white-space: nowrap;
`;
